import { useCallback } from 'react';
import { pick } from 'ramda';
import { useTranslations } from 'js/hooks/i18n';
import { useUserSelector } from 'js/store/hooks';
import { useFormatter } from 'js/hooks/useFormatter';

import { calculate } from '../helpers';
import { BALANCES_NAMES } from '../constants';

export const useGetCustomBalance = () => {
    const formatter = useFormatter();
    const translate = useTranslations();
    const {
        isLoggedIn,
        balance: { balances = {} } = {}
    } = useUserSelector(pick(['isLoggedIn', 'balance']));

    return useCallback((args: string): string | null => {
        if (!isLoggedIn) {
            return null;
        }

        const expressionRegEx = /[a-zA-Z_0-9]+|([+-/*%^])/g;
        const balanceExpression = args.split(',')[0];
        const expressionParts = balanceExpression.match(expressionRegEx) || [];

        let currencyCode = '';
        let invalidCalc = false;
        const allowedOperators = ['+', '-'];
        const disallowedOperators = ['/', '*', '%', '^'];

        const amountParts = expressionParts.map((part) => {
            if (allowedOperators.includes(part)) {
                return part as ('+' | '-');
            }

            if (disallowedOperators.includes(part)) {
                invalidCalc = true;

                return 0;
            }

            const balance = balances[part] || balances[BALANCES_NAMES[part]];

            // XXX - currency code for free spins
            if (!balance || balance.currencyCode === 'XXX') {
                invalidCalc = true;

                return 0;
            }

            currencyCode = currencyCode || balance.currencyCode;

            return Number(balance.amount);
        });

        if (invalidCalc) {
            return translate('playtech.mobile.system.balance.calc.invalid');
        }

        const amount = calculate(amountParts);

        return formatter.formatCurrency(amount, { currencyCode });
    }, [balances, isLoggedIn, formatter, translate]);
};
